import { AApi } from './AApi';
import type { ApiResult, PaginationBody, PaginationParams } from '../types/api.types';
import type {
    AccountBody,
    AccountWrite,
    CheckUserBody,
    LoginBody,
    MicrosoftAuthorizeBody,
    MicrosoftCallbackBody,
    RegisterBody,
    ResetPasswordBody,
    TeamBody,
    TeamDetails,
    TeamInput,
    TfaAppSetupBody,
    UserData,
    UserDetails,
    UserWrite,
    WhoAmIBody,
} from '../types/users.types';
import { TableCacheService } from '../../services/TableCacheService';

/**
 * The Users module: the login and everything around the own account (whoAmI, register, password), plus `/users`
 * (REST, answers `{user, person?}`). Calls that change the login drop the whoAmI cache.
 */
export class UsersApi extends AApi {
    private WhoAmICache: Promise<ApiResult<WhoAmIBody>> | null = null;

    /** POST /changePassword — only logged in; success drops the `password_change` todo and mails a notice. */
    public changePassword(oldPassword: string, newPassword: string): Promise<ApiResult<undefined>> {
        this.WhoAmICache = null;

        return this.Client.post<undefined>(`${this.VersionPath}/changePassword`, {
            old_password: oldPassword,
            new_password: newPassword,
        });
    }

    /** GET /checkUser — `isValid` for an active user, else possibly the address to register with. */
    public checkUser(username: string): Promise<ApiResult<CheckUserBody>> {
        return this.Client.get<CheckUserBody>(`${this.VersionPath}/checkUser`, { username });
    }

    /** POST /login, step 2: the code of the second factor; answers `user` and `todo`. */
    public confirmLogin(code: string): Promise<ApiResult<LoginBody>> {
        this.WhoAmICache = null;
        TableCacheService.clear();

        return this.Client.post<LoginBody>(`${this.VersionPath}/login`, { code });
    }

    /** POST /register, step 2: the code from the mail unlocks the account and logs in; the password follows by mail. */
    public confirmRegistration(username: string, code: string): Promise<ApiResult<RegisterBody>> {
        this.WhoAmICache = null;
        TableCacheService.clear();

        return this.Client.post<RegisterBody>(`${this.VersionPath}/register`, { username, code });
    }

    /** POST /resetPassword, step 2: whether the code from the mail is right — it stays valid for step 3. */
    public checkPasswordReset(username: string, code: string): Promise<ApiResult<ResetPasswordBody>> {
        return this.Client.post<ResetPasswordBody>(`${this.VersionPath}/resetPassword`, { username, code });
    }

    /** POST /resetPassword, step 3: the code again plus the new password. */
    public finishPasswordReset(username: string, code: string, newPassword: string): Promise<ApiResult<ResetPasswordBody>> {
        return this.Client.post<ResetPasswordBody>(`${this.VersionPath}/resetPassword`, { username, code, new_password: newPassword });
    }

    /** POST /login, step 1: user name or primary address plus password; answers `tfa` and sends the code. */
    public login(username: string, password: string): Promise<ApiResult<LoginBody>> {
        this.WhoAmICache = null;
        TableCacheService.clear();

        return this.Client.post<LoginBody>(`${this.VersionPath}/login`, { username, password });
    }

    /** POST /login/microsoft — the `url` of the sign-in at Microsoft; it comes back to the page `/microsoft`. */
    public startMicrosoftLogin(): Promise<ApiResult<MicrosoftAuthorizeBody>> {
        return this.Client.post<MicrosoftAuthorizeBody>(`${this.VersionPath}/login/microsoft`, { origin: window.location.origin });
    }

    /** POST /microsoft/callback — hands over what Microsoft sent back; links the account or logs in (without TFA). */
    public completeMicrosoft(params: { state: string; code?: string; error?: string }): Promise<ApiResult<MicrosoftCallbackBody>> {
        this.WhoAmICache = null;
        TableCacheService.clear();

        return this.Client.post<MicrosoftCallbackBody>(`${this.VersionPath}/microsoft/callback`, params);
    }

    /** GET /logout — ends the session. */
    public logout(): Promise<ApiResult<undefined>> {
        this.WhoAmICache = null;
        TableCacheService.clear();

        return this.Client.get<undefined>(`${this.VersionPath}/logout`);
    }

    /** POST /register, step 1: `x.name` or `x.name@dwc.gmbh`; creates the inactive account and mails a code. */
    public register(username: string): Promise<ApiResult<RegisterBody>> {
        return this.Client.post<RegisterBody>(`${this.VersionPath}/register`, { username });
    }

    /** POST /resetPassword, step 1: mails a code for a new password. */
    public resetPassword(username: string): Promise<ApiResult<ResetPasswordBody>> {
        return this.Client.post<ResetPasswordBody>(`${this.VersionPath}/resetPassword`, { username });
    }

    /** GET /whoAmI — cached until `refresh` or a call that changes the login. */
    public whoAmI(refresh = false): Promise<ApiResult<WhoAmIBody>> {
        if (!refresh && this.WhoAmICache !== null) {
            return this.WhoAmICache;
        }

        // -
        const Pending = this.Client.get<WhoAmIBody>(`${this.VersionPath}/whoAmI`);
        this.WhoAmICache = Pending;

        // A failed call must not stay cached, otherwise one network hiccup would last until reload.
        void Pending.then((result: ApiResult<WhoAmIBody>): void => {
            if (!result.isOk && this.WhoAmICache === Pending) {
                this.WhoAmICache = null;
            }
        });

        // -
        return Pending;
    }

    /** POST /users — always without password (sendPassword gives one); `person_id` or `person`, never both. */
    public createUser(data: UserWrite): Promise<ApiResult<UserData>> {
        return this.Client.post(this.users(), data);
    }

    public deleteUser(userId: number): Promise<ApiResult<undefined>> {
        return this.Client.delete(this.users(userId));
    }

    public getUser(userId: number): Promise<ApiResult<UserDetails>> {
        return this.Client.get(this.users(userId));
    }

    public listUsers(params?: PaginationParams): Promise<ApiResult<{ users: UserData[]; pagination?: PaginationBody }>> {
        return this.Client.get(`${this.users()}${this.toQuery(params)}`);
    }

    /** PUT — every writable field anew; `person` edits the linked person, `person_id` only while none is linked. */
    public replaceUser(userId: number, data: UserWrite): Promise<ApiResult<UserData>> {
        return this.Client.put(this.users(userId), data);
    }

    /** GET /account — the own data as stored, the base of updateAccount(). */
    public getAccount(): Promise<ApiResult<AccountBody>> {
        return this.Client.get(`${this.VersionPath}/account`);
    }

    /** PATCH /account — acronym and colour, the linked person's names; never a new person. */
    public updateAccount(data: AccountWrite): Promise<ApiResult<AccountBody>> {
        return this.Client.patch(`${this.VersionPath}/account`, data);
    }

    /** POST /account/tfaApp/setup — starts setup and returns secret + uri. */
    public startTfaAppSetup(): Promise<ApiResult<TfaAppSetupBody>> {
        return this.Client.post(`${this.VersionPath}/account/tfaApp/setup`);
    }

    /** POST /account/tfaApp/confirm — confirms setup with secret and 6-digit code. */
    public confirmTfaApp(secret: string, code: string): Promise<ApiResult<{ is_linked: boolean }>> {
        return this.Client.post(`${this.VersionPath}/account/tfaApp/confirm`, { secret, code });
    }

    /** POST /account/tfaApp/test — tests the 6-digit code of the linked TFA app. */
    public testTfaApp(code: string): Promise<ApiResult<{ is_valid: boolean }>> {
        return this.Client.post(`${this.VersionPath}/account/tfaApp/test`, { code });
    }

    /** DELETE /account/tfaApp — unlinks the TFA app of the own account. */
    public deleteAccountTfaApp(): Promise<ApiResult<undefined>> {
        return this.Client.delete(`${this.VersionPath}/account/tfaApp`);
    }

    /** POST /account/microsoft — the `url` of the sign-in at Microsoft that links the account to the own one. */
    public startMicrosoftLink(): Promise<ApiResult<MicrosoftAuthorizeBody>> {
        return this.Client.post<MicrosoftAuthorizeBody>(`${this.VersionPath}/account/microsoft`, { origin: window.location.origin });
    }

    /** DELETE /account/microsoft — unlinks the Microsoft account of the own account. */
    public deleteAccountMicrosoft(): Promise<ApiResult<undefined>> {
        return this.Client.delete(`${this.VersionPath}/account/microsoft`);
    }

    /** DELETE /users/{id}/tfaApp — unlinks the TFA app of a user (admin). */
    public deleteUserTfaApp(userId: number): Promise<ApiResult<undefined>> {
        return this.Client.delete(`${this.users(userId)}/tfaApp`);
    }

    /** DELETE /users/{id}/microsoft — unlinks the Microsoft account of a user (admin). */
    public deleteUserMicrosoft(userId: number): Promise<ApiResult<undefined>> {
        return this.Client.delete(`${this.users(userId)}/microsoft`);
    }

    /** POST /persons/{id}/user — the user name from the person's names (`s.huber` or the nickname), active, random colour. */
    public createUserForPerson(personId: number, sendPassword: boolean): Promise<ApiResult<UserData>> {
        return this.Client.post(`${this.VersionPath}/persons/${personId.toString()}/user`, { send_password: sendPassword });
    }

    /** POST /users/{id}/sendPassword — a random password by mail, `password_change` set; 409 without an address. */
    public sendPassword(userId: number): Promise<ApiResult<undefined>> {
        return this.Client.post(`${this.users(userId)}/sendPassword`);
    }

    /** PUT /users/{id}/teams — the stored teams anew; ROOT only by a root, ADMIN brings USER along (API). */
    public replaceUserTeams(userId: number, teamIds: number[]): Promise<ApiResult<{ team_ids: number[]; teams: TeamBody[] }>> {
        return this.Client.put(`${this.users(userId)}/teams`, { team_ids: teamIds });
    }

    /** POST /teams/{id}/users/{userId} — adds one user without replacing the other team assignments. */
    public addUserToTeam(teamId: number, userId: number): Promise<ApiResult<TeamDetails>> {
        return this.Client.post(`${this.teams(teamId)}/users/${userId.toString()}`);
    }

    /** DELETE /teams/{id}/users/{userId} — removes one user from a team. */
    public removeUserFromTeam(teamId: number, userId: number): Promise<ApiResult<TeamDetails>> {
        return this.Client.delete(`${this.teams(teamId)}/users/${userId.toString()}`);
    }

    public listTeams(params?: PaginationParams): Promise<ApiResult<{ teams: TeamBody[]; pagination?: PaginationBody }>> {
        return this.Client.get(`${this.teams()}${this.toQuery(params)}`);
    }

    public getTeam(teamId: number): Promise<ApiResult<TeamDetails>> {
        return this.Client.get(this.teams(teamId));
    }

    public createTeam(data: TeamInput): Promise<ApiResult<{ team: TeamBody }>> {
        return this.Client.post(this.teams(), data);
    }

    public updateTeam(teamId: number, data: TeamInput): Promise<ApiResult<{ team: TeamBody }>> {
        return this.Client.patch(this.teams(teamId), data);
    }

    /** Refused for the built-in teams (`TEAM.protected`) and while users are in it (`TEAM.used`). */
    public deleteTeam(teamId: number): Promise<ApiResult<undefined>> {
        return this.Client.delete(this.teams(teamId));
    }

    /** PATCH — only the fields sent; `person` edits the linked person, `person_id` only while none is linked. */
    public updateUser(userId: number, data: Partial<UserWrite>): Promise<ApiResult<UserData>> {
        return this.Client.patch(this.users(userId), data);
    }

    private teams(teamId?: number): string {
        return `${this.VersionPath}/teams${teamId === undefined ? '' : `/${teamId.toString()}`}`;
    }

    private users(userId?: number): string {
        return `${this.VersionPath}/users${userId === undefined ? '' : `/${userId.toString()}`}`;
    }
}
